// Ported from dev-scrips-pm-react src/billing/components/claim-review/diagnosis-section.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { useState } from 'react';
import { Check, Plus, Search, X } from 'lucide-react';
import { BridgeStatusChip } from '@/billing/bridge';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/billing/bridge';
import { Input } from '@/billing/bridge';
import { showWarning } from '@/shared/lib/toast-helpers';
import {
  useDiagnosisCodeSearch,
  useEncounterAssessmentPlan,
  useUpdateAssessmentPlan,
  type DiagnosisCodeOption,
} from '@/billing/services/use-claim-diagnosis';
import {
  BridgeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/billing/bridge';
import {
  addDiagnosisCondition,
  isDuplicateDiagnosis,
  removeDiagnosisCondition,
  setPrimaryDiagnosis,
} from '@/billing/lib/claim-diagnosis';
import type { InvoiceDetail } from '@/shared/types/billing';
import { BridgeTooltipTrigger as TooltipTrigger, Tooltip, TooltipContent } from '@/billing/bridge';
import { ROW_ICON_BUTTON } from '@/billing/bridge';

/**
 * DIAGNOSIS section — numbered list of diagnoses with code + system + Primary
 * flag, plus add/remove in the editable claim states (PROD-969c phase 2).
 *
 * Flutter parity: `review_claim_popup.dart:2518-2767` (`diagnosisWidget()`).
 * Diagnoses live on the encounter **Assessment & Plan** — editing them is an
 * `/api/Patients/Assessment` write, NOT a billing call. See
 * `docs/CONVERSIONS/claim-969c/CONVERSION_NOTES.md`.
 *
 * ⚠️ Add/remove round-trips the CURRENT full A&P (with medications) fetched via
 * `useEncounterAssessmentPlan` and mutates ONLY the conditions list (see
 * `@/billing/lib/claim-diagnosis`). It never reads from `invoice.assessmentPlan`
 * for the write — that copy has empty meds and would wipe them on save.
 */
interface Props {
  invoice: InvoiceDetail;
  /** Whether add/remove is allowed in the current claim state. */
  editable?: boolean;
  /** Invoice id — forwarded for scoped cache invalidation after a write. */
  invoiceId?: string;
  /**
   * A&P `updateType` — `'PreClaim'` from the Submission flow, `''` otherwise
   * (Flutter `review_claim_popup.dart:2596`). Threaded into the A&P PUT; the
   * popup computes it from `cameFrom`.
   */
  updateType?: string;
  /** Empty-state copy. Claim surface = "No diagnoses on this claim."; the
   *  invoice billing tab passes "No diagnosis available." (Flutter parity). */
  emptyText?: string;
}

/** lg column classes for 1..4 diagnoses (Flutter's Wrap capped at 4 columns). */
const DX_LG_COLS = ['lg:grid-cols-1', 'lg:grid-cols-2', 'lg:grid-cols-3', 'lg:grid-cols-4'] as const;

interface DiagnosisRow {
  identifier?: string | null;
  description?: string;
  code?: string;
  system?: string | null;
  isPrimary?: boolean | null;
}

export function ClaimReviewDiagnosisSection({
  invoice,
  editable = false,
  invoiceId,
  updateType = '',
  emptyText = 'No diagnoses on this claim.',
}: Props) {
  const apQuery = useEncounterAssessmentPlan({
    encounterId: invoice.encounterId,
    patientId: invoice.patientId,
    enabled: editable,
  });
  const ap = apQuery.data;
  const updateAp = useUpdateAssessmentPlan();
  // Block edits while the mutation is in flight OR the A&P is (re)fetching —
  // mutating a stale/incomplete A&P could drop fields silently (Samer review).
  const busy = updateAp.isPending || apQuery.isFetching;

  // Display source is ALWAYS the claim's own diagnosis list (Flutter parity —
  // `review_claim_popup.dart:2519` reads `invoice.diagnosisList`), which carries
  // the correct per-condition `isPrimary`. The editable path used to read this
  // from the separately-fetched encounter A&P, which lags the claim's primary
  // flag — so every row rendered "Secondary" even when the claim (and the API)
  // marked one primary. The encounter A&P (`ap`) is still fetched below, but
  // only to build the add / remove / set-primary mutation payloads; its
  // condition identifiers match the claim's (same server assessment-plan).
  const rows: DiagnosisRow[] = extractDiagnoses(invoice);

  const canEdit = editable && Boolean(ap) && !busy;

  const [addOpen, setAddOpen] = useState(false);
  // Codes already on the claim — the modal marks these "Added" so a second
  // press is a no-op (the mutation also guards via `isDuplicateDiagnosis`).
  const existingCodes = new Set(
    rows.map((r) => (r.code ?? '').toLowerCase()).filter(Boolean),
  );

  const onAdd = (option: DiagnosisCodeOption) => {
    if (!ap) return;
    if (isDuplicateDiagnosis(ap, option)) {
      showWarning('This diagnosis is already on the claim.');
      return;
    }
    updateAp.mutate({
      assessmentPlan: addDiagnosisCondition(ap, option),
      updateType,
      invoiceId,
    });
  };

  const onRemove = (identifier?: string | null) => {
    if (!ap || !identifier) return;
    updateAp.mutate({
      assessmentPlan: removeDiagnosisCondition(ap, identifier),
      updateType,
      invoiceId,
    });
  };

  // Primary/Secondary — makes the picked row the SOLE primary (Flutter
  // Primary/Secondary dropdown). Demoting the only primary is blocked below.
  const onSetPrimary = (identifier?: string | null) => {
    if (!ap || !identifier) return;
    // ATOMIC: `setPrimaryDiagnosis` returns the FULL A&P with the target set
    // primary and every other condition set non-primary, and we PUT that whole
    // object in ONE request. There is no separate demote-then-promote pair, so
    // there's no window where the claim ends up with zero (or two) primaries —
    // the single write always lands exactly one.
    updateAp.mutate({
      assessmentPlan: setPrimaryDiagnosis(ap, identifier),
      updateType,
      invoiceId,
    });
  };

  return (
    <section className="rounded-xl border border-border-default bg-surface-card px-6 py-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-text-primary">
          Diagnosis
        </h3>
        {editable ? (
          <button
            type="button"
            aria-label="Add diagnosis"
            disabled={!ap || busy}
            onClick={() => setAddOpen(true)}
            className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full text-brand-primary hover:bg-brand-primary/10 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" aria-hidden />
          </button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm leading-5 text-text-muted">{emptyText}</p>
      ) : (
        // Flutter lays diagnoses out in a Wrap capped at 4 columns, each cell
        // = totalWidth / min(count, 4) (`review_claim_popup.dart:2530, 2623-2636`).
        // So ≤4 diagnoses share one row (3 → 3-up); >4 wrap at 4. Mirrored with a
        // CSS grid whose column count is min(count, 4); collapses to 1/2 columns
        // on narrow widths (Flutter's isCompact → 2).
        <ol
          // PM sets the lg column count through a runtime `--dx-cols` property and an arbitrary grid template; the PA
          // forbids both, so the count min(rows, 4) picks one of four named utilities (same result).
          className={`grid gap-x-6 gap-y-4 grid-cols-1 sm:grid-cols-2 ${DX_LG_COLS[Math.min(rows.length, 4) - 1]}`}
        >
          {rows.map((d, idx) => (
            <li
              key={d.identifier ?? `${d.code ?? 'd'}-${idx}`}
              className="flex items-start gap-3 min-w-0"
            >
              <span className="flex-shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-grey-100 text-xs font-medium leading-none text-text-secondary">
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm leading-5 text-text-primary font-medium">
                  {d.description ?? '—'}
                </div>
                <div className="font-mono text-xs leading-5 text-text-muted mt-1">
                  {[d.code, d.system].filter(Boolean).join(' • ') || '—'}
                </div>
                {/* Primary/Secondary. A claim must always have EXACTLY ONE
                    primary, so the primary row is NOT demotable — it renders a
                    locked "Primary" label with no dropdown. To change the
                    principal diagnosis you promote a Secondary row, which
                    auto-demotes the current primary (setPrimaryDiagnosis). Only
                    Secondary rows (editable) get the dropdown. Flutter gates
                    this on `isAddDeleteDiagnosis` (:2807-2863). */}
                {editable && d.identifier && !d.isPrimary ? (
                  <Select
                    value="Secondary"
                    disabled={!canEdit}
                    onValueChange={(v) => {
                      if (v === 'Primary') onSetPrimary(d.identifier);
                    }}
                  >
                    <SelectTrigger className="mt-1 h-8 w-32 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Primary">Primary</SelectItem>
                      <SelectItem value="Secondary">Secondary</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="mt-1">
                    <BridgeStatusChip semantic="neutral" tone="soft" size="xs">
                      {d.isPrimary ? 'Primary' : 'Secondary'}
                    </BridgeStatusChip>
                  </div>
                )}
              </div>
              {editable && d.identifier ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        aria-label={`Remove diagnosis ${d.code ?? ''}`}
                        onClick={() => onRemove(d.identifier)}
                        disabled={!canEdit}
                        className={`${ROW_ICON_BUTTON} text-status-error`}
                      />
                    }
                  >
                    <X className="size-4" aria-hidden />
                  </TooltipTrigger>
                  <TooltipContent>Remove diagnosis</TooltipContent>
                </Tooltip>
              ) : null}
            </li>
          ))}
        </ol>
      )}

      {editable ? (
        <DiagnosisAddDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          disabled={!ap || busy}
          existingCodes={existingCodes}
          onPick={onAdd}
        />
      ) : null}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Add Diagnosis modal — ICD-10 search list, each row a "+" that adds it
// immediately (API per press) while the dialog STAYS OPEN so several can be
// added in one session. Closing the dialog never writes. Flutter parity
// (`review_claim_popup.dart` Add Diagnosis dialog).
// ──────────────────────────────────────────────────────────────────────────

function DiagnosisAddDialog({
  open,
  onOpenChange,
  disabled,
  existingCodes,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled: boolean;
  /** Lower-cased codes already on the claim — shown as "Added". */
  existingCodes: Set<string>;
  onPick: (option: DiagnosisCodeOption) => void;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  // Empty query returns the default ICD-10 list, so results show on open.
  const searchQ = useDiagnosisCodeSearch(debounced, open);
  const results = searchQ.data ?? [];
  const oneChar = debounced.trim().length === 1;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing the dialog is close-only — it never triggers a write. Reset
        // the query so the next open starts clean.
        if (!next) setQuery('');
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Diagnosis</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search
            aria-hidden
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-hint"
          />
          <Input
            type="text"
            autoFocus
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10"
            autoComplete="off"
          />
        </div>

        <div className="max-h-80 overflow-y-auto scrollbar-hide divide-y divide-border-default">
          {oneChar ? (
            <div className="px-1 py-3 text-sm leading-5 text-text-muted">
              Keep typing to search…
            </div>
          ) : searchQ.isFetching && results.length === 0 ? (
            <div className="px-1 py-3 text-sm leading-5 text-text-muted">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-1 py-3 text-sm leading-5 text-text-muted">
              No matches{debounced.trim() ? ` for "${debounced.trim()}"` : ''}.
            </div>
          ) : (
            results.map((r) => {
              const added = existingCodes.has(r.code.toLowerCase());
              return (
                <div
                  key={r.id}
                  className="flex items-center gap-3 py-2.5 pl-1 pr-1"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm leading-5 text-text-primary leading-snug">
                      {r.display}
                    </div>
                    <div className="font-mono text-xs leading-5 text-text-muted mt-1">{r.code}</div>
                  </div>
                  {added ? (
                    <span
                      className="shrink-0 inline-flex items-center gap-1 text-xs font-medium leading-5 text-status-success"
                      aria-label={`${r.code} already added`}
                    >
                      <Check className="w-4 h-4" aria-hidden /> Added
                    </span>
                  ) : (
                    <button
                      type="button"
                      aria-label={`Add ${r.code}`}
                      disabled={disabled}
                      // Adds immediately (API per press); dialog stays open so
                      // multiple diagnoses can be added in one session.
                      onClick={() => onPick(r)}
                      className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full text-brand-primary hover:bg-brand-primary/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-5 h-5" aria-hidden />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Read-only diagnosis rows from the invoice snapshot (`assessmentPlan`).
 * Used when the section is not editable, or before the full A&P loads.
 */
function extractDiagnoses(invoice: InvoiceDetail): DiagnosisRow[] {
  // Prefer the claim's `diagnosisList` (Flutter's source — carries the correct
  // isPrimary); fall back to the invoice's embedded assessment-plan conditions
  // if the list is absent. Both share the same per-condition shape (identifier /
  // conditionCode / isPrimary).
  const hasClaimList =
    Array.isArray(invoice.diagnosisList) && invoice.diagnosisList.length > 0;
  // `diagnosisList` is the claim-authoritative source and should be present on
  // any claim that has diagnoses. If it's missing while the embedded A&P has
  // conditions, the fallback keeps the section populated — but surface it in
  // dev so a real missing-data / wire-shape regression isn't masked silently.
  if (
    !hasClaimList &&
    import.meta.env.DEV &&
    (invoice.assessmentPlan?.assessmentPlanCondition?.length ?? 0) > 0
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      '[claim-diagnosis] invoice.diagnosisList absent; falling back to assessmentPlan.assessmentPlanCondition — verify the invoice wire shape.',
    );
  }
  // Guard the runtime shape defensively (no Zod at fetch boundaries in this
  // codebase — DTOs are wire-typed): the Array.isArray check + optional chaining
  // below degrade a schema mismatch to an empty/"—" render rather than a throw.
  const conditions = hasClaimList
    ? invoice.diagnosisList
    : invoice.assessmentPlan?.assessmentPlanCondition;
  if (!Array.isArray(conditions)) return [];
  return conditions
    .filter((c) => !c.isDeleted)
    .map((c) => ({
      identifier: c.identifier,
      description: c.conditionCode?.display ?? c.conditionCode?.code,
      code: c.conditionCode?.code,
      system: c.conditionCode?.system,
      isPrimary: c.isPrimary,
    }));
}
